/**
 * Auto-generated service - DO NOT EDIT MANUALLY
 * Generated from: asset.ts
 * Run: pnpm gen
 */

import { API_METHODS } from '../constants/api-methods';
import { getApiPath } from '../path';
import { requirePostForm, type RequestOptions, type Transport } from '../transport';

import type {
  CreateAssetRequest,
  CreateAssetResponse,
  DownloadAssetFromArcusRequest,
  DownloadAssetFromArcusResponse,
  DownloadAssetRequest,
  DownloadAssetResponse,
  DownloadCallConsoleAssetRequest,
  DownloadCallConsoleAssetResponse,
  GetAssetRequest,
  GetAssetResponse,
  ListAssetsRequest,
  ListAssetsResponse,
  ListCallConsoleAssetsRequest,
  ListCallConsoleAssetsResponse,
  UpdateAssetInArcusRequest,
  UpdateAssetInArcusResponse,
} from '../types/asset';

export const createAssetService = (transport: Transport) => ({
  async listAssets(
    request: ListAssetsRequest,
    options: RequestOptions = {},
  ): Promise<ListAssetsResponse> {
    const { organization_id, program_id, case_record_id, ...params } = request;
    const url = getApiPath(API_METHODS.AssetService.ListAssets, {
      organization_id,
      program_id,
      case_record_id,
    });
    return transport.get<ListAssetsResponse>(url, { params, ...options });
  },

  async getAsset(
    request: GetAssetRequest,
    options: RequestOptions = {},
  ): Promise<GetAssetResponse> {
    const { organization_id, program_id, asset_id, case_record_id, ...params } = request;
    const url = getApiPath(API_METHODS.AssetService.GetAsset, {
      organization_id,
      program_id,
      asset_id,
      case_record_id,
    });
    return transport.get<GetAssetResponse>(url, { params, ...options });
  },

  async downloadAsset(
    request: DownloadAssetRequest,
    options: RequestOptions = {},
  ): Promise<DownloadAssetResponse> {
    const { organization_id, program_id, asset_id, case_record_id, ...params } = request;
    const url = getApiPath(API_METHODS.AssetService.DownloadAsset, {
      organization_id,
      program_id,
      asset_id,
      case_record_id,
    });
    return transport.get<DownloadAssetResponse>(url, { params, ...options });
  },

  async downloadAssetFromArcus(
    request: DownloadAssetFromArcusRequest,
    options: RequestOptions = {},
  ): Promise<DownloadAssetFromArcusResponse> {
    const { organization_id, program_id, asset_id, case_record_id, ...params } = request;
    const url = getApiPath(API_METHODS.AssetService.DownloadAssetFromArcus, {
      organization_id,
      program_id,
      asset_id,
      case_record_id,
    });
    return transport.get<DownloadAssetFromArcusResponse>(url, { params, ...options });
  },

  async createAsset(
    request: CreateAssetRequest,
    options: RequestOptions = {},
  ): Promise<CreateAssetResponse> {
    const { organization_id, program_id, case_record_id, ...data } = request;
    const url = getApiPath(API_METHODS.AssetService.CreateAsset, {
      organization_id,
      program_id,
      case_record_id,
    });
    const formData = new FormData();
    if (data.data) {
      // Handle File, Blob, or Uint8Array (from proto bytes)
      const fileValue = data.data instanceof Blob ? data.data : new Blob([data.data] as BlobPart[]);
      formData.append('data', fileValue);
    }
    if (data.filename !== undefined) {
      formData.append('filename', String(data.filename));
    }
    if (data.content_type !== undefined) {
      formData.append('content_type', String(data.content_type));
    }
    if (data.asset_type !== undefined) {
      formData.append('asset_type', String(data.asset_type));
    }
    if (data.field_id !== undefined) {
      formData.append('field_id', String(data.field_id));
    }
    const postForm = requirePostForm(transport, 'AssetService.CreateAsset');
    return postForm<CreateAssetResponse>(url, formData, options);
  },

  async updateAssetInArcus(
    request: UpdateAssetInArcusRequest,
    options: RequestOptions = {},
  ): Promise<UpdateAssetInArcusResponse> {
    const { organization_id, program_id, asset_id, case_record_id, ...data } = request;
    const url = getApiPath(API_METHODS.AssetService.UpdateAssetInArcus, {
      organization_id,
      program_id,
      asset_id,
      case_record_id,
    });
    const formData = new FormData();
    if (data.data) {
      // Handle File, Blob, or Uint8Array (from proto bytes)
      const fileValue = data.data instanceof Blob ? data.data : new Blob([data.data] as BlobPart[]);
      formData.append('data', fileValue);
    }
    if (data.filename !== undefined) {
      formData.append('filename', String(data.filename));
    }
    if (data.content_type !== undefined) {
      formData.append('content_type', String(data.content_type));
    }
    if (data.asset_type !== undefined) {
      formData.append('asset_type', String(data.asset_type));
    }
    if (data.metadata !== undefined) {
      formData.append('metadata', String(data.metadata));
    }
    const postForm = requirePostForm(transport, 'AssetService.UpdateAssetInArcus');
    return postForm<UpdateAssetInArcusResponse>(url, formData, options);
  },

  async listCallConsoleAssets(
    request: ListCallConsoleAssetsRequest,
    options: RequestOptions = {},
  ): Promise<ListCallConsoleAssetsResponse> {
    const { organization_id, program_id, case_record_id, call_id } = request;
    const url = getApiPath(API_METHODS.AssetService.ListCallConsoleAssets, {
      organization_id,
      program_id,
      case_record_id,
      call_id,
    });
    return transport.get<ListCallConsoleAssetsResponse>(url, options);
  },

  async downloadCallConsoleAsset(
    request: DownloadCallConsoleAssetRequest,
    options: RequestOptions = {},
  ): Promise<DownloadCallConsoleAssetResponse> {
    const { organization_id, program_id, case_record_id, call_id, asset_id, ...params } = request;
    const url = getApiPath(API_METHODS.AssetService.DownloadCallConsoleAsset, {
      organization_id,
      program_id,
      case_record_id,
      call_id,
      asset_id,
    });
    return transport.get<DownloadCallConsoleAssetResponse>(url, { params, ...options });
  },
});

export type AssetService = ReturnType<typeof createAssetService>;
